/**
 * Forgot password — both web portals.
 *
 *   email ──► a 6-digit code goes to it (Clerk's `reset_password_email_code`)
 *        ──► the code + the new password ──► signed in with it
 *
 * An account that also has a second factor cannot finish a reset here (Clerk
 * asks for it after the new password) — that is a `ResetFlowError`, for the
 * screen to say so. A session the device still holds is signed out before the
 * code goes (`retryAfterStaleSession`). Nothing throws and nothing is put into words here.
 */

import { useClerk, useSignIn } from '@clerk/clerk-react';
import { useCallback, useState } from 'react';

import { retryAfterStaleSession } from './stale-session';

export type PasswordResetStep = { step: 'email' } | { step: 'reset'; email: string };

export type PasswordResetOutcome =
  | { outcome: 'next' }
  /** The password is changed and the session is active. */
  | { outcome: 'complete' }
  | { outcome: 'failed'; error: unknown };

export class ResetFlowError extends Error {
  constructor(readonly code: 'second_factor_required' | 'not_loaded' | 'not_on_reset' | string) {
    super(`Password reset cannot continue: ${code}`);
    this.name = 'ResetFlowError';
  }
}

const fail = (error: unknown): PasswordResetOutcome => ({ outcome: 'failed', error });

export function usePasswordReset() {
  const { signIn, isLoaded, setActive } = useSignIn();
  const clerk = useClerk();
  const [state, setState] = useState<PasswordResetStep>({ step: 'email' });

  const send = useCallback(
    async (email: string): Promise<PasswordResetOutcome> => {
      if (!isLoaded || !signIn) return fail(new ResetFlowError('not_loaded'));
      try {
        await retryAfterStaleSession(
          () => signIn.create({ strategy: 'reset_password_email_code', identifier: email }),
          () => clerk.signOut()
        );
        setState({ step: 'reset', email });
        return { outcome: 'next' };
      } catch (error) {
        return fail(error);
      }
    },
    [isLoaded, signIn, clerk]
  );

  const reset = useCallback(
    async ({
      code,
      password,
    }: {
      code: string;
      password: string;
    }): Promise<PasswordResetOutcome> => {
      if (!signIn || state.step !== 'reset') return fail(new ResetFlowError('not_on_reset'));
      try {
        const result = await signIn.attemptFirstFactor({
          strategy: 'reset_password_email_code',
          code,
          password,
        });
        if (result.status === 'needs_second_factor') {
          return fail(new ResetFlowError('second_factor_required'));
        }
        if (result.status !== 'complete')
          return fail(new ResetFlowError(`status_${result.status}`));
        await setActive?.({ session: result.createdSessionId });
        return { outcome: 'complete' };
      } catch (error) {
        return fail(error);
      }
    },
    [signIn, state, setActive]
  );

  /** A new code to the same address. */
  const resend = useCallback(async (): Promise<PasswordResetOutcome> => {
    if (!signIn || state.step !== 'reset') return fail(new ResetFlowError('not_on_reset'));
    try {
      await retryAfterStaleSession(
        () => signIn.create({ strategy: 'reset_password_email_code', identifier: state.email }),
        () => clerk.signOut()
      );
      return { outcome: 'next' };
    } catch (error) {
      return fail(error);
    }
  }, [signIn, state, clerk]);

  const restart = useCallback(() => setState({ step: 'email' }), []);

  return { state, ready: isLoaded && !!signIn, send, reset, resend, restart };
}
